// The sign-in editor's SSO providers over the settings document (SCR-154): one row per provider,
// add/edit values, validation per type, status. Pure and DOM-free. Nothing here holds a secret: a
// provider carries only `secret_name`, the name under which the proxy keeps the client secret its
// admin enters there (SCR-484). Ids are the server's: a new provider goes out with no id, an
// existing one keeps the id it was read with; neither is ever shown.
import type { FieldErrors } from "../../primitives/forms";
import type { ChipTone } from "../../primitives/page";
import { getPath, isObject, type Json, type JsonObject } from "../../settings/inherit";
import { HTTPS_URL, lines, strings, text } from "./json-values";

export type ProviderType = "identity_center_saml" | "okta" | "google" | "entra" | "generic_saml" | "generic_oidc";
export type ProviderKind = "saml" | "oidc";

export const PROVIDER_TYPES: readonly { value: ProviderType; label: string; kind: ProviderKind }[] = [
  { value: "identity_center_saml", label: "AWS IAM Identity Center (SAML)", kind: "saml" },
  { value: "okta", label: "Okta (OIDC)", kind: "oidc" },
  { value: "google", label: "Google Workspace (OIDC)", kind: "oidc" },
  { value: "entra", label: "Microsoft Entra ID (OIDC)", kind: "oidc" },
  { value: "generic_saml", label: "Other SAML 2.0", kind: "saml" },
  { value: "generic_oidc", label: "Other OpenID Connect", kind: "oidc" },
];

export const PROVIDERS_MAX = 20;
export const LABEL_MAX = 64;
const SCOPES_MAX = 20;
const DOMAINS_MAX = 100;
const GROUPS_MAX = 500;
const GROUP_MAX = 256;

// The settings schema's scope and e-mail domain patterns.
const SCOPE = /^[A-Za-z0-9_.:/-]{1,128}$/;
const DOMAIN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

export function kindOf(type: string): ProviderKind {
  return PROVIDER_TYPES.find((t) => t.value === type)?.kind ?? "oidc";
}

export function typeLabel(type: string): string {
  return PROVIDER_TYPES.find((t) => t.value === type)?.label ?? type;
}

/** One provider as the form edits it: lists one per line. Ids, secret names and team mappings ride along untouched. */
export interface ProviderValues {
  /** The server's id; "" for a provider not saved yet. */
  id: string;
  type: ProviderType;
  label: string;
  enabled: boolean;
  metadataUrl: string;
  issuer: string;
  clientId: string;
  scopes: string;
  groupsAttribute: string;
  allowedEmailDomains: string;
  allowedGroups: string;
  /** The secret's name on the proxy, read-only here; null until the proxy names it. */
  secretName: string | null;
  teamMappings: Json;
}

export const DEFAULT_SCOPES = ["openid", "email", "profile"];

export function blankProvider(type: ProviderType): ProviderValues {
  return {
    id: "",
    type,
    label: "Sign in with SSO",
    enabled: true,
    metadataUrl: "",
    issuer: "",
    clientId: "",
    scopes: DEFAULT_SCOPES.join("\n"),
    groupsAttribute: "",
    allowedEmailDomains: "",
    allowedGroups: "",
    secretName: null,
    teamMappings: [],
  };
}

export function providersOf(document: Json | undefined): JsonObject[] {
  const list = getPath(document, "sign_in.sso_providers");
  return Array.isArray(list) ? list.filter(isObject) : [];
}

export function providerFromDoc(doc: JsonObject): ProviderValues {
  const saml = isObject(doc["saml"]) ? doc["saml"] : {};
  const oidc = isObject(doc["oidc"]) ? doc["oidc"] : {};
  return {
    id: text(doc["id"]),
    type: (text(doc["type"]) || "generic_saml") as ProviderType,
    label: text(doc["label"]),
    enabled: doc["enabled"] !== false,
    metadataUrl: text(saml["metadata_url"]),
    issuer: text(oidc["issuer"]),
    clientId: text(oidc["client_id"]),
    scopes: Array.isArray(oidc["scopes"]) ? strings(oidc["scopes"]) : DEFAULT_SCOPES.join("\n"),
    groupsAttribute: text(doc["groups_attribute"]),
    allowedEmailDomains: strings(doc["allowed_email_domains"]),
    allowedGroups: strings(doc["allowed_groups"]),
    secretName: typeof doc["secret_name"] === "string" ? doc["secret_name"] : null,
    teamMappings: doc["team_mappings"] ?? [],
  };
}

/** The provider as the document holds it: only the block its type reads, no id while it has none. */
export function providerToDoc(values: ProviderValues): JsonObject {
  const doc: JsonObject = {};
  if (values.id !== "") doc["id"] = values.id;
  doc["type"] = values.type;
  doc["label"] = values.label.trim();
  doc["enabled"] = values.enabled;
  if (kindOf(values.type) === "saml") doc["saml"] = { metadata_url: values.metadataUrl.trim() };
  else doc["oidc"] = { issuer: values.issuer.trim(), client_id: values.clientId.trim(), scopes: lines(values.scopes) };
  doc["groups_attribute"] = values.groupsAttribute.trim() === "" ? null : values.groupsAttribute.trim();
  doc["allowed_email_domains"] = lines(values.allowedEmailDomains);
  doc["allowed_groups"] = lines(values.allowedGroups);
  doc["team_mappings"] = values.teamMappings;
  doc["secret_name"] = values.secretName;
  return doc;
}

function listProblem(values: readonly string[], max: number, pattern: RegExp | null, longest: number, noun: string): string | null {
  if (values.length > max) return `At most ${max} ${noun}s.`;
  if (new Set(values).size !== values.length) return `A ${noun} is listed twice.`;
  for (const value of values) {
    if (value.length > longest) return `A ${noun} is longer than ${longest} characters.`;
    if (pattern && !pattern.test(value)) return `"${value}" is not a valid ${noun}.`;
  }
  return null;
}

/** Errors by field name for one provider's values, per its type. */
export function validateProvider(values: ProviderValues): FieldErrors {
  const errors: FieldErrors = {};
  const label = values.label.trim();
  if (label === "" || label.length > LABEL_MAX) errors["label"] = `Between 1 and ${LABEL_MAX} characters.`;
  if (kindOf(values.type) === "saml") {
    if (!HTTPS_URL.test(values.metadataUrl.trim())) errors["metadataUrl"] = "An https URL with no query or fragment.";
  } else {
    if (!HTTPS_URL.test(values.issuer.trim())) errors["issuer"] = "An https URL with no query or fragment.";
    const clientId = values.clientId.trim();
    if (clientId === "" || clientId.length > 256) errors["clientId"] = "Between 1 and 256 characters.";
    const scopes = listProblem(lines(values.scopes), SCOPES_MAX, SCOPE, 128, "scope");
    if (scopes !== null) errors["scopes"] = scopes;
  }
  const attribute = values.groupsAttribute.trim();
  if (attribute.length > 256) errors["groupsAttribute"] = "At most 256 characters.";
  const domains = listProblem(lines(values.allowedEmailDomains), DOMAINS_MAX, DOMAIN, 253, "domain");
  if (domains !== null) errors["allowedEmailDomains"] = domains;
  const groups = listProblem(lines(values.allowedGroups), GROUPS_MAX, null, GROUP_MAX, "group");
  if (groups !== null) errors["allowedGroups"] = groups;
  return errors;
}

export interface ProviderStatus {
  tone: ChipTone;
  text: string;
}

/**
 * What the list shows beside a provider. Whether the secret is present in the proxy's account is
 * the proxy's to say (SCR-484), so an OIDC provider is "configured" once it names one.
 */
export function providerStatus(doc: JsonObject): ProviderStatus {
  if (doc["enabled"] === false) return { tone: "grey", text: "disabled" };
  if (kindOf(text(doc["type"])) === "saml") {
    return isObject(doc["saml"]) ? { tone: "green", text: "configured" } : { tone: "red", text: "metadata URL missing" };
  }
  if (!isObject(doc["oidc"])) return { tone: "red", text: "OIDC settings missing" };
  if (typeof doc["secret_name"] !== "string") return { tone: "amber", text: "waiting for its client secret" };
  return { tone: "green", text: "configured" };
}

/** The URL a provider is read from, for the list. */
export function providerUrl(doc: JsonObject): string {
  const saml = isObject(doc["saml"]) ? doc["saml"] : null;
  const oidc = isObject(doc["oidc"]) ? doc["oidc"] : null;
  return text(saml?.["metadata_url"]) || text(oidc?.["issuer"]);
}

